import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";

// Portal uses a SEPARATE axios instance because it relies on httpOnly cookies,
// not the recruiter JWT stored in localStorage.
const portalHttp = axios.create({ baseURL: API, withCredentials: true });

export default function CandidatePortal() {
  const [state, setState] = useState({ loading: true, me: null, error: null });
  const [offer, setOffer] = useState(null);
  const [responding, setResponding] = useState(false);
  const [respondMsg, setRespondMsg] = useState(null);
  const processedRef = useRef(false);

  // 1. Handle magic-link OR Google OAuth callback BEFORE anything else.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const magic = q.get("magic");
    if (magic && !processedRef.current) {
      processedRef.current = true;
      (async () => {
        try {
          await portalHttp.post("/candidate-portal/magic-link/verify", { token: magic });
          window.history.replaceState(null, "", "/portal");
          const r = await portalHttp.get("/candidate-portal/me");
          setState({ loading: false, me: r.data, error: null });
        } catch (e) {
          setState({ loading: false, me: null, error: e.response?.data?.detail || e.message });
        }
      })();
      return;
    }
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m && !processedRef.current) {
      processedRef.current = true;
      (async () => {
        try {
          await portalHttp.post(
            "/candidate-portal/session",
            {},
            { headers: { "X-Session-ID": m[1] } },
          );
          window.location.hash = "";
          const r = await portalHttp.get("/candidate-portal/me");
          setState({ loading: false, me: r.data, error: null });
        } catch (e) {
          setState({ loading: false, me: null, error: e.response?.data?.detail || e.message });
        }
      })();
      return;
    }
    portalHttp
      .get("/candidate-portal/me")
      .then((r) => setState({ loading: false, me: r.data, error: null }))
      .catch(() => setState({ loading: false, me: null, error: null }));
  }, []);

  // 2. Load offer whenever candidate identified
  useEffect(() => {
    if (!state.me) return;
    portalHttp.get("/candidate-portal/my-offer").then((r) => setOffer(r.data));
  }, [state.me]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await portalHttp.post("/candidate-portal/logout");
    setState({ loading: false, me: null, error: null });
    setOffer(null);
  };

  // LinkedIn PDF + Resume self-service uploads
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMsg, setPdfMsg] = useState(null);
  const [pdfErr, setPdfErr] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null); // parsed fields awaiting user confirm
  const pdfRef = useRef(null);
  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPdfBusy(true); setPdfMsg(null); setPdfErr(null); setPdfPreview(null);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await portalHttp.post("/candidate-portal/linkedin-pdf/preview", form);
      setPdfPreview({
        filename: r.data.filename,
        headline: r.data.headline || "",
        location: r.data.location || "",
        summary: r.data.summary || "",
        skills: r.data.skills || [],
        experience: r.data.experience || [],
        education: r.data.education || [],
        certifications: r.data.certifications || [],
        rawText: r.data.rawText,
      });
    } catch (err) {
      setPdfErr(err.response?.data?.detail || err.message);
    } finally {
      setPdfBusy(false);
      if (pdfRef.current) pdfRef.current.value = "";
    }
  };

  const commitPdf = async () => {
    if (!pdfPreview) return;
    setPdfBusy(true); setPdfErr(null);
    try {
      const r = await portalHttp.post("/candidate-portal/linkedin-pdf/commit", pdfPreview);
      const c = r.data?.counts || {};
      const bits = [
        pdfPreview.headline && "headline",
        pdfPreview.location && "location",
        c.skills && `${c.skills} skills`,
        c.experience && `${c.experience} roles`,
        c.education && `${c.education} education`,
      ].filter(Boolean).join(", ");
      setPdfMsg(`Thanks! We saved your LinkedIn profile (${bits || "text saved"}) for the hiring team.`);
      setPdfPreview(null);
    } catch (err) {
      setPdfErr(err.response?.data?.detail || err.message);
    } finally {
      setPdfBusy(false);
    }
  };

  const cancelPdfPreview = () => {
    setPdfPreview(null);
    setPdfErr(null);
  };

  const [cvBusy, setCvBusy] = useState(false);
  const [cvMsg, setCvMsg] = useState(null);
  const [cvErr, setCvErr] = useState(null);
  const [cvPreview, setCvPreview] = useState(null); // {filename, text, chars} while previewing
  const [cvEdited, setCvEdited] = useState("");
  const cvRef = useRef(null);

  const uploadCv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCvBusy(true); setCvMsg(null); setCvErr(null); setCvPreview(null);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await portalHttp.post("/candidate-portal/resume/preview", form);
      setCvPreview(r.data);
      setCvEdited(r.data.text);
    } catch (err) {
      setCvErr(err.response?.data?.detail || err.message);
    } finally {
      setCvBusy(false);
      if (cvRef.current) cvRef.current.value = "";
    }
  };

  const commitCv = async () => {
    if (!cvPreview) return;
    setCvBusy(true); setCvErr(null);
    try {
      const r = await portalHttp.post("/candidate-portal/resume/commit", {
        text: cvEdited,
        filename: cvPreview.filename,
      });
      setCvMsg(`✓ CV submitted (${r.data.filename} · ${r.data.chars.toLocaleString()} chars). The hiring team can now review it.`);
      setCvPreview(null);
      setCvEdited("");
    } catch (err) {
      setCvErr(err.response?.data?.detail || err.message);
    } finally {
      setCvBusy(false);
    }
  };

  const cancelCvPreview = () => {
    setCvPreview(null);
    setCvEdited("");
    setCvErr(null);
  };

  const respond = async (accepted) => {
    const reason = window.prompt(
      accepted ? "Optional acceptance note?" : "Reason for declining? (optional)",
      accepted ? "" : "",
    );
    if (reason === null) return;
    setResponding(true);
    setRespondMsg(null);
    try {
      const r = await portalHttp.post("/candidate-portal/respond", { accepted, reason });
      setRespondMsg(
        accepted
          ? "🎉 Offer accepted. Your recruiter has been notified."
          : "Offer declined. Thank you for letting us know.",
      );
      // reload offer state
      const off = await portalHttp.get("/candidate-portal/my-offer");
      setOffer(off.data);
      // eslint-disable-next-line no-unused-vars
      const _ = r;
    } catch (e) {
      setRespondMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setResponding(false);
    }
  };

  if (state.loading) {
    return <PortalShell><div className="text-slate-500 text-sm">Loading…</div></PortalShell>;
  }

  // --------- LOGIN VIEW ----------
  if (!state.me) {
    return (
      <PortalShell>
        <PublicJobBoard />
        <MagicLinkLogin onGoogle={login} loginError={state.error} />
      </PortalShell>
    );
  }

  // --------- AUTHENTICATED CANDIDATE ----------
  const off = offer?.offer;
  const response = offer?.offerResponse;
  const status = offer?.status;

  return (
    <PortalShell>
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="card p-6 flex items-center justify-between" data-testid="portal-header">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400">Candidate Portal</div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-1">
              Hi {state.me.name} 👋
            </h1>
            <div className="text-sm text-slate-500 mt-0.5">
              {state.me.position && <>Applying for <strong>{state.me.position}</strong> · </>}
              Status: <span className="pill pill-offer">{status}</span>
            </div>
          </div>
          <button className="btn btn-outline" onClick={logout} data-testid="portal-logout">Sign out</button>
        </div>

        {/* Self-service uploads: LinkedIn PDF + Resume */}
        <div className="card p-5 space-y-4" data-testid="portal-uploads-card">
          <div>
            <div className="label-eyebrow">Speed up your application</div>
            <h2 className="font-display text-lg font-bold mt-1">Share your profile</h2>
            <p className="text-sm text-slate-500 mt-1">
              Give the hiring team the fastest possible picture of you. Uploads are private to the team reviewing your application.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* LinkedIn PDF */}
            <div className="rounded-md border border-[#0A66C2]/25 bg-[#0A66C2]/[0.04] p-3 flex flex-col" data-testid="portal-linkedin-card">
              <div className="text-xs font-semibold text-[#0A66C2] uppercase tracking-wider">LinkedIn PDF</div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed flex-1">
                Open your LinkedIn profile → <strong>More</strong> → <strong>Save to PDF</strong> → upload here. We&apos;ll auto-fill your headline, location, skills, experience and education.
              </div>
              <input
                ref={pdfRef} type="file" accept=".pdf"
                data-testid="portal-linkedin-pdf-input"
                onChange={uploadPdf}
                className="hidden"
              />
              <button
                data-testid="portal-linkedin-pdf-btn"
                className="btn btn-primary mt-3 whitespace-nowrap !bg-[#0A66C2] hover:!bg-[#084d92]"
                onClick={() => pdfRef.current?.click()}
                disabled={pdfBusy || !!pdfPreview}
              >
                {pdfBusy && !pdfPreview ? "Extracting…" : pdfPreview ? "Reviewing…" : "Upload LinkedIn PDF"}
              </button>
              {pdfMsg && (
                <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800" data-testid="portal-linkedin-pdf-success">
                  {pdfMsg}
                </div>
              )}
              {pdfErr && !pdfPreview && (
                <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-800" data-testid="portal-linkedin-pdf-error">
                  {pdfErr}
                </div>
              )}
            </div>

            {/* Full CV / Resume */}
            <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3 flex flex-col" data-testid="portal-cv-card">
              <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wider">Full CV / Resume</div>
              <div className="text-xs text-slate-600 mt-1 leading-relaxed flex-1">
                Upload your full resume as a <strong>PDF</strong>, <strong>DOCX</strong>, or <strong>TXT</strong> file (up to 8 MB). You&apos;ll get a chance to review and edit the extracted text before it&apos;s sent to the hiring team.
              </div>
              <input
                ref={cvRef} type="file" accept=".pdf,.docx,.txt,.md"
                data-testid="portal-cv-input"
                onChange={uploadCv}
                className="hidden"
              />
              <button
                data-testid="portal-cv-btn"
                className="btn btn-primary mt-3 whitespace-nowrap !bg-emerald-700 hover:!bg-emerald-800"
                onClick={() => cvRef.current?.click()}
                disabled={cvBusy || !!cvPreview}
              >
                {cvBusy && !cvPreview ? "Extracting…" : cvPreview ? "Reviewing…" : "Upload Your CV"}
              </button>
              {cvMsg && (
                <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800" data-testid="portal-cv-success">
                  {cvMsg}
                </div>
              )}
              {cvErr && !cvPreview && (
                <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-800" data-testid="portal-cv-error">
                  {cvErr}
                </div>
              )}
            </div>
          </div>

          {pdfPreview && (
            <div className="rounded-md border border-[#0A66C2]/40 bg-white p-4 mt-3" data-testid="portal-linkedin-preview">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#0A66C2] font-semibold flex items-center gap-2">
                    Review your LinkedIn profile
                    {pdfPreview.aiEnhanced && (
                      <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium" data-testid="portal-linkedin-ai-badge">
                        AI-enhanced
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    <strong>{pdfPreview.filename}</strong> — fix anything the parser got wrong, add missing bits, then submit.
                    {pdfPreview.aiEnhanced && (typeof pdfPreview.yearsExperience === "number") && (
                      <> · {pdfPreview.yearsExperience}+ years experience detected</>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-800 text-xs underline"
                  onClick={cancelPdfPreview}
                  data-testid="portal-linkedin-cancel"
                >
                  Cancel
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow !text-[10px]">Headline</label>
                  <input
                    className="input"
                    data-testid="portal-linkedin-headline"
                    value={pdfPreview.headline}
                    onChange={(e) => setPdfPreview({ ...pdfPreview, headline: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label-eyebrow !text-[10px]">Location</label>
                  <input
                    className="input"
                    data-testid="portal-linkedin-location"
                    value={pdfPreview.location}
                    onChange={(e) => setPdfPreview({ ...pdfPreview, location: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="label-eyebrow !text-[10px]">Summary</label>
                <textarea
                  className="input"
                  rows={4}
                  data-testid="portal-linkedin-summary"
                  value={pdfPreview.summary}
                  onChange={(e) => setPdfPreview({ ...pdfPreview, summary: e.target.value })}
                />
              </div>
              <div className="mt-3">
                <label className="label-eyebrow !text-[10px]">Skills</label>
                <input
                  className="input"
                  placeholder="Comma-separated (e.g. Python, React, Oracle HCM)"
                  data-testid="portal-linkedin-skills"
                  value={(pdfPreview.skills || []).join(", ")}
                  onChange={(e) => setPdfPreview({
                    ...pdfPreview,
                    skills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })}
                />
                {pdfPreview.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {pdfPreview.skills.map((s, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-[#0A66C2]/10 text-[#0A66C2] rounded-full">{s}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3">
                <label className="label-eyebrow !text-[10px]">Experience ({pdfPreview.experience.length})</label>
                {pdfPreview.experience.length === 0 ? (
                  <div className="text-[11px] text-slate-400">Parser found no roles. You can leave this and add roles from your profile page later.</div>
                ) : (
                  <div className="space-y-2">
                    {pdfPreview.experience.map((exp, i) => (
                      <div key={i} className="border rounded-sm p-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                        <input
                          className="input !py-1 !text-xs"
                          placeholder="Title"
                          value={exp.title || ""}
                          onChange={(e) => {
                            const list = [...pdfPreview.experience];
                            list[i] = { ...list[i], title: e.target.value };
                            setPdfPreview({ ...pdfPreview, experience: list });
                          }}
                        />
                        <input
                          className="input !py-1 !text-xs"
                          placeholder="Company"
                          value={exp.company || ""}
                          onChange={(e) => {
                            const list = [...pdfPreview.experience];
                            list[i] = { ...list[i], company: e.target.value };
                            setPdfPreview({ ...pdfPreview, experience: list });
                          }}
                        />
                        <input
                          className="input !py-1 !text-xs"
                          placeholder="Period"
                          value={exp.period || ""}
                          onChange={(e) => {
                            const list = [...pdfPreview.experience];
                            list[i] = { ...list[i], period: e.target.value };
                            setPdfPreview({ ...pdfPreview, experience: list });
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3">
                <label className="label-eyebrow !text-[10px]">Education ({pdfPreview.education.length})</label>
                {pdfPreview.education.length === 0 ? (
                  <div className="text-[11px] text-slate-400">Parser found no education entries.</div>
                ) : (
                  <div className="space-y-2">
                    {pdfPreview.education.map((e0, i) => (
                      <div key={i} className="border rounded-sm p-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        <input
                          className="input !py-1 !text-xs"
                          placeholder="School"
                          value={e0.school || ""}
                          onChange={(ev) => {
                            const list = [...pdfPreview.education];
                            list[i] = { ...list[i], school: ev.target.value };
                            setPdfPreview({ ...pdfPreview, education: list });
                          }}
                        />
                        <input
                          className="input !py-1 !text-xs"
                          placeholder="Degree"
                          value={e0.degree || ""}
                          onChange={(ev) => {
                            const list = [...pdfPreview.education];
                            list[i] = { ...list[i], degree: ev.target.value };
                            setPdfPreview({ ...pdfPreview, education: list });
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {pdfErr && (
                <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-800">{pdfErr}</div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="btn btn-outline !text-xs"
                  onClick={cancelPdfPreview}
                  disabled={pdfBusy}
                  data-testid="portal-linkedin-preview-cancel"
                >
                  Discard
                </button>
                <button
                  className="btn btn-primary !text-xs !bg-[#0A66C2] hover:!bg-[#084d92]"
                  onClick={commitPdf}
                  disabled={pdfBusy}
                  data-testid="portal-linkedin-submit"
                >
                  {pdfBusy ? "Submitting…" : "Submit to hiring team"}
                </button>
              </div>
            </div>
          )}

          {cvPreview && (
            <div className="rounded-md border border-emerald-300 bg-white p-4 mt-3" data-testid="portal-cv-preview">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs uppercase tracking-wider text-emerald-800 font-semibold">Review your CV</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    <strong>{cvPreview.filename}</strong> · {cvEdited.length.toLocaleString()} chars.
                    Edit anything below (typos, wrong page order, etc.) then submit.
                  </div>
                </div>
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-800 text-xs underline"
                  onClick={cancelCvPreview}
                  data-testid="portal-cv-cancel"
                >
                  Cancel
                </button>
              </div>
              <textarea
                className="input text-xs font-mono leading-relaxed"
                style={{ minHeight: 260 }}
                value={cvEdited}
                onChange={(e) => setCvEdited(e.target.value)}
                data-testid="portal-cv-editor"
              />
              {cvErr && (
                <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-800">{cvErr}</div>
              )}
              <div className="flex justify-end gap-2 mt-3">
                <button
                  className="btn btn-outline !text-xs"
                  onClick={cancelCvPreview}
                  disabled={cvBusy}
                  data-testid="portal-cv-preview-cancel"
                >
                  Discard
                </button>
                <button
                  className="btn btn-primary !text-xs !bg-emerald-700 hover:!bg-emerald-800"
                  onClick={commitCv}
                  disabled={cvBusy || cvEdited.trim().length < 20}
                  data-testid="portal-cv-submit"
                >
                  {cvBusy ? "Submitting…" : "Submit to hiring team"}
                </button>
              </div>
            </div>
          )}
        </div>

        {!off && (
          <div className="card p-6 text-center" data-testid="portal-no-offer">
            <div className="text-slate-500 text-sm">
              You do not have an active offer yet. We&apos;ll notify you here when one is extended.
            </div>
          </div>
        )}

        {off && (
          <div className="card p-6 space-y-4" data-testid="portal-offer">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Your Offer</h2>
              <span className={`pill ${off.state === "extended" ? "pill-offer" : off.state === "accepted" ? "pill-hired" : "pill-rejected"}`}>
                {off.state}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Position" value={off.position || state.me.position} />
              <Info label="Salary" value={off.salary ? `${off.salary} ${off.currency}` : "—"} />
              <Info label="Start Date" value={off.startDate || "—"} />
              <Info label="Offer Expires" value={off.expiryDate || "—"} />
            </div>
            {off.notes && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Package Details</div>
                <div className="text-sm text-slate-700 bg-slate-50 border rounded-sm p-3 whitespace-pre-wrap">{off.notes}</div>
              </div>
            )}

            {response ? (
              <div className={`rounded-md border p-4 text-sm ${response.accepted ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`} data-testid="portal-response-recorded">
                {response.accepted ? "✅" : "❌"} You {response.accepted ? "accepted" : "declined"} this offer on {new Date(response.respondedAt).toLocaleString()}.
                {response.reason && <div className="text-xs mt-1">Your note: &ldquo;{response.reason}&rdquo;</div>}
              </div>
            ) : (
              off.state === "extended" && (
                <div className="flex gap-3 pt-2 border-t">
                  <button
                    data-testid="portal-accept"
                    className="btn btn-primary flex-1 !py-2.5"
                    onClick={() => respond(true)}
                    disabled={responding}
                  >
                    ✅ Accept Offer
                  </button>
                  <button
                    data-testid="portal-reject"
                    className="btn btn-outline flex-1 !py-2.5 !text-red-700 !border-red-300 hover:!bg-red-50"
                    onClick={() => respond(false)}
                    disabled={responding}
                  >
                    Decline Offer
                  </button>
                </div>
              )
            )}
            {respondMsg && (
              <div className="text-sm text-slate-700 bg-slate-50 border p-3 rounded-sm">{respondMsg}</div>
            )}
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function PublicJobBoard() {
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    let alive = true;
    portalHttp.get("/candidate-portal/public-jobs")
      .then((r) => { if (alive) { setJobs(r.data.items || []); setLoading(false); } })
      .catch(() => { if (alive) { setJobs([]); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto card p-6 mb-6 text-center text-sm text-slate-500" data-testid="portal-jobs-loading">
        Loading open positions…
      </div>
    );
  }
  if (!jobs || jobs.length === 0) {
    return null;
  }

  return (
    <div className="max-w-2xl mx-auto card p-6 mb-6" data-testid="portal-public-jobs">
      <div className="mb-3">
        <div className="text-xs uppercase tracking-wider text-slate-400">Open positions</div>
        <h2 className="font-display text-xl font-bold text-slate-900 mt-1">Apply to a role</h2>
        <p className="text-sm text-slate-500 mt-1">
          Browse our current openings and apply directly — no account needed. We&apos;ll email you once your application is received.
        </p>
      </div>
      <div className="space-y-2" data-testid="portal-jobs-list">
        {jobs.map((j) => (
          <div key={j.RequisitionNumber} className="border rounded-md p-3 flex items-center justify-between gap-2" data-testid={`portal-job-${j.RequisitionNumber}`}>
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{j.Title || "Untitled role"}</div>
              <div className="text-[11px] text-slate-500 font-mono">
                Req #{j.RequisitionNumber}
                {j.PostingDate && ` · Posted ${j.PostingDate}`}
              </div>
              {j.Description && (
                <div className="text-xs text-slate-600 mt-1 line-clamp-2">{j.Description}</div>
              )}
            </div>
            <button
              className="btn btn-primary !text-xs whitespace-nowrap"
              onClick={() => setSelectedJob(j)}
              data-testid={`portal-job-apply-${j.RequisitionNumber}`}
            >
              Apply
            </button>
          </div>
        ))}
      </div>
      {selectedJob && (
        <ApplyModal job={selectedJob} onClose={() => setSelectedJob(null)} />
      )}
    </div>
  );
}

function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", file: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const canSubmit = form.name.trim() && form.email.trim() && form.file && !busy;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("requisitionNumber", String(job.RequisitionNumber));
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("file", form.file);
      const r = await portalHttp.post("/candidate-portal/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" data-testid="portal-apply-modal">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        {done ? (
          <div className="text-center space-y-3" data-testid="portal-apply-done">
            <div className="text-4xl">🎉</div>
            <h3 className="font-display text-xl font-bold">Application received</h3>
            <p className="text-sm text-slate-600">
              Thanks — your application for <strong>{job.Title}</strong> (Req #{job.RequisitionNumber}) has been sent to the hiring team.
              You&apos;ll get a confirmation email at <strong>{form.email}</strong> shortly.
            </p>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-lg font-bold">Apply — {job.Title}</h3>
                <div className="text-[11px] text-slate-500 font-mono">Req #{job.RequisitionNumber}</div>
              </div>
              <button className="text-slate-400 hover:text-slate-700 text-xl" onClick={onClose}>×</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-eyebrow !text-[10px]">Full name</label>
                <input
                  className="input"
                  data-testid="portal-apply-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="label-eyebrow !text-[10px]">Email</label>
                <input
                  className="input"
                  type="email"
                  data-testid="portal-apply-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="label-eyebrow !text-[10px]">CV / Resume (PDF, DOCX or TXT, up to 8 MB)</label>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,.md"
                  data-testid="portal-apply-file"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                  className="text-xs"
                />
                {form.file && (
                  <div className="text-[11px] text-slate-500 mt-1">
                    {form.file.name} · {(form.file.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </div>
              {err && (
                <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800">{err}</div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={submit}
                  disabled={!canSubmit}
                  data-testid="portal-apply-submit"
                >
                  {busy ? "Submitting…" : "Submit Application"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}



function MagicLinkLogin({ onGoogle, loginError }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const request = async () => {
    setBusy(true); setErr(null);
    try {
      await portalHttp.post("/candidate-portal/magic-link", { email: email.trim() });
      setSent(true);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto card p-8" data-testid="portal-login-card">
      <h1 className="font-display text-3xl font-bold text-slate-900 text-center">Candidate Portal</h1>
      <p className="text-slate-500 mt-2 text-sm text-center">
        Sign in to view and respond to your offer.
      </p>
      {loginError && (
        <div className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="portal-login-error">
          {loginError}
        </div>
      )}

      {sent ? (
        <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="magic-link-sent">
          <div>✉️ If <strong>{email}</strong> is registered as a candidate, a sign-in link has been emailed to you. Open your inbox and click the link (expires in 15 minutes).</div>
          <div className="mt-2 text-xs text-emerald-900/80">
            <strong>Not in inbox?</strong> Check <strong>Spam / Junk</strong> — the sender <code>onboarding@resend.dev</code> is often filtered by Gmail/Outlook.
          </div>
          <button className="text-xs text-slate-500 hover:underline mt-3 block" onClick={() => { setSent(false); setEmail(""); }}>
            Use a different email
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6">
            <label className="label-eyebrow block mb-1.5">Email address</label>
            <input
              data-testid="magic-link-email"
              type="email"
              className="input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && email.trim() && request()}
              autoFocus
            />
            {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
          </div>
          <button
            data-testid="magic-link-submit"
            className="mt-3 btn btn-primary w-full !py-2.5"
            onClick={request}
            disabled={busy || !email.trim()}
          >
            {busy ? "Sending…" : "Email me a sign-in link"}
          </button>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 border-t border-slate-200"></div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">or</div>
            <div className="flex-1 border-t border-slate-200"></div>
          </div>

          <button
            data-testid="portal-google-login"
            className="btn btn-outline w-full !py-2.5"
            onClick={onGoogle}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" style={{ display: "inline-block", marginRight: 8 }}>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </button>
        </>
      )}
      <div className="text-xs text-slate-400 mt-6 text-center">Vaku HCM Recruit · Candidate portal</div>
    </div>
  );
}

function PortalShell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="border-b bg-white/70 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#002FA7] text-white grid place-items-center font-bold">V</div>
          <div>
            <div className="text-sm font-semibold leading-none">Vaku HCM</div>
            <div className="text-[10px] text-slate-500">Recruit · Agentic AI</div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {children}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}
